import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, CalendarDays, ClipboardList, ScissorsLineDashed, Users, LogOut, Sparkles, KeyRound, UserCog, Images, Users2, Wallet, Coins, Settings as SettingsIcon, Menu, Shield, FileText, Target } from "lucide-react";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import EditProfileModal from "@/components/EditProfileModal";
import { LEVEL_RANK, LEVELS } from "@/lib/permissions";

// iter104 — Persisted sidebar-collapse state. `false` = expanded (default),
// `true` = icon-only mode. Kept in `localStorage` so admin operators keep
// their preferred layout across page reloads. SSR-safe fallback if window
// isn't available at first render.
const SIDEBAR_LS_KEY = "kahani.sidebar.collapsed";
function readCollapsed() {
  try { return typeof window !== "undefined" && window.localStorage.getItem(SIDEBAR_LS_KEY) === "1"; }
  catch { return false; }
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try { window.localStorage.setItem(SIDEBAR_LS_KEY, collapsed ? "1" : "0"); }
    catch { /* ignore quota / privacy-mode failures */ }
  }, [collapsed]);

  const isViewer = user?.role === "viewer";
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isOwner = !!user?.is_owner;
  const permMap = user?.permissions_effective || {};

  // iter184 — Single canonical nav registry. Every entry carries the
  // module slug it belongs to; the permission engine (owners + role
  // defaults + per-user overrides) is the ONLY thing that decides
  // visibility. Legacy role-based branches were removed so a
  // coordinator granted `Leads=Edit` immediately sees Leads, and a
  // coordinator whose Admin explicitly sets `Team=No Access` loses it.
  //
  // Some items (Backup, Payment Proofs, Users, Settings, Financials)
  // remain owner/admin-only by design — those are intentionally not
  // exposed to the RBAC UI. They map to `role: 'admin'` below and are
  // gated in addition to any module check.
  const ALL_ITEMS = [
    { to: "/",                 icon: LayoutDashboard,     label: "Dashboard",           end: true, testid: "nav-dashboard",       module: "dashboard" },
    { to: "/leads",            icon: Target,              label: "Leads",                          testid: "nav-leads",           module: "leads" },
    { to: "/bookings",         icon: ClipboardList,       label: "Bookings",                        testid: "nav-bookings",        module: "bookings" },
    { to: "/post-production",  icon: ScissorsLineDashed,  label: "Post-Production",                testid: "nav-postproduction",  module: "post_production" },
    { to: "/team",             icon: Users2,              label: "Team",                            testid: "nav-team",            module: "team" },
    { to: "/gallery",          icon: Images,              label: "AI Gallery",                      testid: "nav-gallery",         module: "gallery" },
    { to: "/calendar",         icon: CalendarDays,        label: "Calendar",                        testid: "nav-calendar",        module: "dashboard" },
    { to: "/office-expenses",  icon: Wallet,              label: "Office Expenses",                testid: "nav-office-expenses", module: "cash_book" },
    { to: "/financials",       icon: Coins,               label: "Financials & Payroll",            testid: "nav-financials",      module: "financial_center", adminOnly: true },
    { to: "/my-earnings",      icon: Coins,               label: "My Earnings",                     testid: "nav-my-earnings",     roles: ["admin", "coordinator", "manager", "editor", "photographer", "cinematographer", "freelancer", "staff"] },
    { to: "/backup",           icon: Shield,              label: "Backup",                          testid: "nav-backup",          adminOnly: true },
    { to: "/payment-proofs",   icon: FileText,            label: "Payment Proofs",                  testid: "nav-payment-proofs",  adminOnly: true },
    { to: "/users",            icon: Users,               label: "Users",                           testid: "nav-users",           module: "users", adminOnly: true },
    { to: "/settings",         icon: SettingsIcon,        label: "Settings",                        testid: "nav-settings",        module: "settings", adminOnly: true },
  ];

  const items = useMemo(() => {
    return ALL_ITEMS.filter((it) => {
      // Admin-only surfaces need the actual admin/owner role.
      if (it.adminOnly && !isAdmin && !isOwner) return false;
      // Role-restricted item (e.g. My Earnings for non-admins).
      if (it.roles && !it.roles.includes(user?.role)) return false;
      // Owners short-circuit — see everything they're role-eligible for.
      if (isOwner) return true;
      // Legacy carve-outs preserved for backward compatibility:
      //   Viewer sees only Bookings (unchanged from iter104).
      //   Manager: no Bookings / Leads / financial surfaces (iter137).
      if (isViewer && it.to !== "/bookings") return false;
      if (isManager && ["/bookings", "/leads", "/office-expenses"].includes(it.to)) return false;
      // Module-permission gate (the primary check).
      if (it.module) {
        const lv = permMap[it.module] || LEVELS.NO_ACCESS;
        if ((LEVEL_RANK[lv] ?? 0) < LEVEL_RANK.view_only) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permMap, isOwner, isAdmin, isManager, isViewer, user?.role]);

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-stone-50" data-sidebar-collapsed={collapsed ? "1" : "0"}>
      {/* Sidebar — width animates between expanded (16rem) and collapsed (4rem) */}
      <aside
        className={`${collapsed ? "w-16" : "w-64"} border-r border-stone-200 bg-white flex-col hidden md:flex transition-[width] duration-200 ease-out`}
        data-testid="app-sidebar"
      >
        <div className={`h-16 flex items-center border-b border-stone-200 ${collapsed ? "px-0 justify-center" : "px-5"}`}>
          <Sparkles className={`w-5 h-5 text-rose-700 ${collapsed ? "" : "mr-2"}`} strokeWidth={1.8} />
          {!collapsed && (
            <div className="flex flex-col leading-tight">
              <span className="font-heading font-semibold text-stone-900 text-sm">Kahani</span>
              <span className="text-[10px] uppercase tracking-wider text-stone-500">by Rakesh Janardan</span>
            </div>
          )}
        </div>
        <nav className={`flex-1 space-y-1 ${collapsed ? "p-2" : "p-3"}`}>
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.testid}
              title={collapsed ? it.label : undefined}
              className={({ isActive }) =>
                "nav-item" + (isActive ? " active" : "") + (collapsed ? " justify-center px-2" : "")
              }
            >
              <it.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              {!collapsed && <span className="truncate">{it.label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className={`border-t border-stone-200 ${collapsed ? "p-2" : "p-3"}`}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-1">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-700 to-amber-600 text-white flex items-center justify-center text-xs font-semibold" title={user?.name || ""}>
                {(user?.name || "U").slice(0, 1).toUpperCase()}
              </div>
              <button onClick={() => setShowEditProfile(true)} data-testid="edit-profile-button" className="btn-ghost p-1.5 rounded-md" title="Edit profile">
                <UserCog className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <button onClick={() => setShowChangePwd(true)} data-testid="change-password-button" className="btn-ghost p-1.5 rounded-md" title="Change password">
                <KeyRound className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <button onClick={doLogout} data-testid="logout-button" className="btn-ghost p-1.5 rounded-md" title="Logout">
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 px-2 py-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-700 to-amber-600 text-white flex items-center justify-center text-xs font-semibold">
                {(user?.name || "U").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-stone-900 truncate" data-testid="sidebar-user-name">{user?.name}</div>
                <div className="text-[11px] uppercase tracking-wider text-stone-500">{user?.role}</div>
              </div>
              <button onClick={() => setShowEditProfile(true)} data-testid="edit-profile-button" className="btn-ghost p-1.5 rounded-md" title="Edit profile">
                <UserCog className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <button onClick={() => setShowChangePwd(true)} data-testid="change-password-button" className="btn-ghost p-1.5 rounded-md" title="Change password">
                <KeyRound className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <button onClick={doLogout} data-testid="logout-button" className="btn-ghost p-1.5 rounded-md" title="Logout">
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-stone-200 bg-white/70 backdrop-blur-xl flex items-center px-6 sticky top-0 z-40">
          {/* iter104 — Hamburger toggle. On mobile (`md:` hidden), the sidebar
               is already hidden so the button acts as a "reveal soon" no-op —
               tapped state still persists and applies once the ≥md breakpoint
               kicks in. On desktop it flips between icon-only and full modes. */}
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="btn-ghost p-2 rounded-md mr-3 hover:bg-stone-100"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label="Toggle sidebar"
            aria-expanded={!collapsed}
            data-testid="sidebar-toggle-btn"
          >
            <Menu className="w-5 h-5 text-stone-700" strokeWidth={1.8} />
          </button>
          <div className="md:hidden font-heading font-semibold">Kahani by Rakesh Janardan</div>
          <div className="ml-auto flex items-center gap-4">
            <div className="text-xs text-stone-500 hidden sm:block">
              {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>

      <ChangePasswordModal open={showChangePwd} onClose={() => setShowChangePwd(false)} />
      <EditProfileModal open={showEditProfile} onClose={() => setShowEditProfile(false)} />
    </div>
  );
}
